import React, { useState, useEffect, useRef } from 'react';
import AnswerFields from './AnswerFields';
import { updateHomework, deleteHomework } from '../../services/adminService';
import { uploadHomeworkImage, uploadHomeworkFile } from '../../services/uploadService';
import RichEditor from '../Common/RichEditor';
import { confirmDialog } from '../Common/confirm';
import { FileIcon, fileColor, fmtSize } from '../Common/fileKind';
import Collapse from '../Common/Collapse';
import toast from 'react-hot-toast';

const buildEmptyGrid = (rows, cols) => Array.from({ length: rows }, () => Array(cols).fill(''));
const rebuildGrid = (rows, cols, old) => Array.from({ length: rows }, (_, r) =>
  Array.from({ length: cols }, (_, c) => old?.[r]?.[c] ?? '')
);

const fromHw = (hw) => {
  const aType = hw?.answerType || 'single';
  const gRows = hw?.gridRows || 3;
  const gCols = hw?.gridCols || 2;
  let grid = buildEmptyGrid(gRows, gCols);
  if (hw && aType === 'grid') {
    try {
      const flat = JSON.parse(hw.correctAnswer || '[]');
      grid = Array.from({ length: gRows }, (_, r) => Array.from({ length: gCols }, (_, c) => (Array.isArray(flat) ? flat[r * gCols + c] : '') ?? ''));
    } catch { /* keep empty */ }
  }
  return {
    form: {
      title: hw?.title || '',
      description: hw?.description || '',
      correctAnswer: aType === 'single' ? (hw?.correctAnswer || '') : '',
      anyOfAnswers: !!hw?.anyOfAnswers,
      answerType: aType, gridRows: gRows, gridCols: gCols,
      textSolution: hw?.textSolution || '',
      solutionImage: hw?.solutionImage || '', // legacy — не показываем, но сохраняем как есть
      videoSolution: hw?.videoSolution || '',
      maxScore: hw?.maxScore ?? 1,
      manualReview: !!hw?.manualReview,
    },
    grid,
    files: hw?.attachedFiles || [],
    solFiles: hw?.attachedSolutionFiles || [],
  };
};

// Одна карточка-номер: свёрнутая строка + раскрытая форма редактирования.
// Сохранение — АВТОМАТИЧЕСКОЕ (debounce), без кнопки. Публикация (в родителе) делает
// номер видимым ученику. dragRef/dragStyle/rowProps — для перетаскивания (sortable).
const HomeworkNumberEditor = React.memo(({ homework, defaultTitle, expanded, onToggle, onDeleted, onDuplicate, onDirty, onSaved, index = 0, showScore = false, defaultMaxScore = 1, dragRef, dragStyle, rowProps }) => {
  const hasId = !!homework?.id;
  const init = fromHw(homework);
  const [formData, setFormData] = useState(!hasId ? { ...init.form, title: defaultTitle || '', maxScore: defaultMaxScore } : init.form);
  const [gridAnswers, setGridAnswers] = useState(init.grid);
  const [attachedFiles, setAttachedFiles] = useState(init.files);
  const [attachedSolutionFiles, setAttachedSolutionFiles] = useState(init.solFiles);
  const [formKey, setFormKey] = useState(0);

  // Пересобираем при смене редактируемого номера. На ПЕРВОМ маунте состояние уже из props —
  // не трогаем (иначе создание нового объекта formData ложно триггерит onDirty = «изменение»).
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    const n = fromHw(homework);
    setFormData(!hasId ? { ...n.form, title: defaultTitle || '', maxScore: defaultMaxScore } : n.form);
    setGridAnswers(n.grid); setAttachedFiles(n.files); setAttachedSolutionFiles(n.solFiles);
    setFormKey(k => k + 1);
  }, [homework?.id]); // eslint-disable-line

  // ── Автосохранение ──────────────────────────────────────────────────────────
  // Собираем payload из текущего состояния (замыкание рендера).
  const buildPayload = () => {
    const serializedAnswer = formData.answerType === 'grid' ? JSON.stringify(gridAnswers.flat()) : formData.correctAnswer;
    return {
      title: formData.title, description: formData.description,
      correctAnswer: serializedAnswer,
      answerType: formData.answerType, gridRows: formData.gridRows, gridCols: formData.gridCols,
      textSolution: formData.textSolution, videoSolution: formData.videoSolution,
      solutionImage: formData.solutionImage || '',
      attachedFiles, attachedSolutionFiles,
      manualReview: !!formData.manualReview,
      anyOfAnswers: formData.answerType === 'single' && !formData.manualReview && !!formData.anyOfAnswers,
      ...(showScore ? { maxScore: Number(formData.maxScore) || 1 } : {}),
    };
  };
  const saveTimer = useRef(null);
  const dirtyRef = useRef(false);
  // flushSave переопределяется каждый рендер → всегда пишет актуальное состояние.
  const flushSave = useRef(async () => {});
  flushSave.current = async () => {
    if (!homework?.id || !dirtyRef.current) return;
    dirtyRef.current = false;
    const payload = buildPayload();
    // Родитель держит список номеров — отдаём ему свежие поля сразу, иначе
    // при повторном открытии ДЗ номер соберётся из старого списка и опустеет.
    onSaved && onSaved(homework.id, payload);
    try { await updateHomework(homework.id, payload); }
    catch { dirtyRef.current = true; /* тихо — следующее изменение сохранит снова */ }
  };
  const scheduleSave = () => {
    if (!homework?.id) return;
    dirtyRef.current = true;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flushSave.current(), 700);
  };
  // Дозапись при закрытии/размонтировании (переключение номера, уход со страницы).
  useEffect(() => () => { clearTimeout(saveTimer.current); flushSave.current(); }, []); // eslint-disable-line

  // Любое реальное изменение: снять с публикации (родитель) + запланировать автосохранение.
  const touch = () => { onDirty && onDirty(); scheduleSave(); };
  const set = (patch) => { setFormData(prev => ({ ...prev, ...patch })); touch(); };

  const handleAddFile = async (file, type = 'homework') => {
    try {
      toast.loading('Загрузка файла...', { id: 'uploadFile' });
      const response = await uploadHomeworkFile(file);
      if (response.success) {
        const fileData = { url: response.data.url, name: response.data.originalName || file.name, size: file.size, type: file.type };
        if (type === 'homework') setAttachedFiles(prev => [...prev, fileData]);
        else setAttachedSolutionFiles(prev => [...prev, fileData]);
        touch();
        toast.success('Файл добавлен', { id: 'uploadFile' });
      }
    } catch { toast.error('Ошибка загрузки файла', { id: 'uploadFile' }); }
  };

  const handleDelete = async () => {
    if (!hasId) { onDeleted && onDeleted(); return; }
    if (await confirmDialog({ title: 'Удалить номер?', message: 'Задание и ответы учеников на него будут удалены.', confirmText: 'Удалить' })) {
      dirtyRef.current = false; clearTimeout(saveTimer.current); // отменяем висящее автосохранение
      try { await deleteHomework(homework.id); toast.success('Номер удалён'); onDeleted && onDeleted(); }
      catch { toast.error('Ошибка при удалении'); }
    }
  };

  const handleDuplicate = () => {
    if (!onDuplicate) return;
    // afterId — копия встанет сразу под этим номером
    onDuplicate({ ...buildPayload(), title: (formData.title || `№${index + 1}`), afterId: homework.id });
  };

  // короткое превью ответа для свёрнутой строки
  const answerPreview = () => {
    if (formData.manualReview) return 'Ручная проверка';
    if (formData.answerType === 'grid') return 'Сетка';
    return formData.correctAnswer ? `Ответ: ${formData.correctAnswer}` : 'Без ответа';
  };

  const imgUpload = async (file) => {
    try { toast.loading('Загрузка изображения...', { id: 'imgup' }); const r = await uploadHomeworkImage(file); if (r && r.success) { toast.success('Изображение добавлено', { id: 'imgup' }); return r.data.url; } toast.dismiss('imgup'); }
    catch { toast.error('Ошибка загрузки', { id: 'imgup' }); }
    return null;
  };

  return (
    <div ref={dragRef} style={dragStyle} className={`hn-item${expanded ? ' open' : ''}`}>
      <div className="hn-row" onClick={() => onToggle(homework?.id)} {...(rowProps || {})}>
        <span className="hn-chev"><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></span>
        <span className="hn-badge">{index + 1}</span>
        <span className="hn-title">{formData.title || `№${index + 1}`}</span>
        <span className="hn-meta">{answerPreview()}</span>
        {onDuplicate && (
          <button className="hn-del" title="Дублировать номер" onClick={(e) => { e.stopPropagation(); handleDuplicate(); }} onPointerDown={e => e.stopPropagation()}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="8" width="12" height="12" rx="2"/><path d="M4 16V6a2 2 0 0 1 2-2h10"/></svg>
          </button>
        )}
        <button className="hn-del" title="Удалить номер" onClick={(e) => { e.stopPropagation(); handleDelete(); }} onPointerDown={e => e.stopPropagation()}>
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
        </button>
      </div>

      <Collapse open={expanded}>
        <div className="hn-body">
          <div className="fl-grid2">
            <div className="fl">
              <label className="fl-label">Заголовок номера</label>
              <input className="fl-input" value={formData.title} onChange={(e) => set({ title: e.target.value })} placeholder="№1" />
            </div>
            {showScore && (
              <div className="fl">
                <label className="fl-label">Баллы за номер</label>
                <input className="fl-input" type="number" min="1" value={formData.maxScore} onChange={(e) => set({ maxScore: e.target.value })} />
              </div>
            )}
          </div>

          <div className="fl">
            <label className="fl-label">Текст задания</label>
            <RichEditor
              key={formKey}
              value={formData.description}
              onChange={(v) => { if (v !== formData.description) touch(); setFormData(fd => ({ ...fd, description: v })); }}
              placeholder=""
              onImageUpload={imgUpload}
              onAttachFile={(f) => handleAddFile(f, 'homework')}
            />
            {attachedFiles.length > 0 && (
              <div className="mat-list2" style={{ marginTop: 8 }}>
                {attachedFiles.map((file, i) => (
                  <div key={i} className="mat-item2">
                    <span className="mat-ic2" style={{ background: fileColor(file.name)[0], color: fileColor(file.name)[1] }}><FileIcon name={file.name} size={18} /></span>
                    <a className="mat-name2" href={file.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-1)', textDecoration: 'none' }}>{file.name}</a>
                    {fmtSize(file.size) && <span className="mat-size2">{fmtSize(file.size)}</span>}
                    <button className="mat-del2" aria-label="Удалить" onClick={() => { setAttachedFiles(prev => prev.filter((_, k) => k !== i)); touch(); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="fl-grid2">
            <div className="fl">
              <label className="fl-label">Тип ответа</label>
              <div className="seg-toggle">
                {[{ key: 'single', label: 'Одно значение' }, { key: 'grid', label: 'Сетка' }].map(opt => (
                  <button key={opt.key} type="button" disabled={formData.manualReview}
                    className={formData.answerType === opt.key ? 'active' : ''}
                    onClick={() => { touch(); setFormData(fd => ({ ...fd, answerType: opt.key, correctAnswer: '' })); setGridAnswers(buildEmptyGrid(formData.gridRows, formData.gridCols)); }}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {/* Ручная проверка: эталона нет, ученик присылает решение целиком,
                  а баллы ставит куратор в разделе «Проверка ДЗ». */}
              <button type="button" className={`hn-manual${formData.manualReview ? ' on' : ''}`}
                onClick={() => { touch(); setFormData(fd => ({ ...fd, manualReview: !fd.manualReview, correctAnswer: '' })); }}>
                <span className="hn-manual-dot" />
                Ручная проверка
                <span className="hn-manual-sub">{formData.manualReview ? 'баллы ставит куратор' : 'ответ сверяется автоматически'}</span>
              </button>
            </div>
            {formData.answerType === 'single' && !formData.manualReview && (
              <div className="fl">
                <label className="fl-label">Правильный ответ</label>
                <AnswerFields
                  value={formData.correctAnswer}
                  onChange={(v) => set({ correctAnswer: v })}
                  anyOf={formData.anyOfAnswers}
                  onAnyOfChange={(v) => set({ anyOfAnswers: v })}
                  inputClass="fl-input" />
              </div>
            )}
          </div>

          {formData.answerType === 'grid' && !formData.manualReview && (
            <div className="fl">
              <label className="fl-label">Сетка ответов</label>
              <div style={{ display: 'flex', gap: 16, marginBottom: 12, alignItems: 'center' }}>
                <label style={{ fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 8 }}>Строки
                  <input type="number" min="1" max="20" value={formData.gridRows} onChange={e => { touch(); const r = Math.max(1, Math.min(20, +e.target.value || 1)); setFormData(fd => ({ ...fd, gridRows: r })); setGridAnswers(prev => rebuildGrid(r, formData.gridCols, prev)); }} style={{ width: 64, padding: '6px 8px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--card)', color: 'var(--text-1)' }} />
                </label>
                <label style={{ fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 8 }}>Столбцы
                  <input type="number" min="1" max="6" value={formData.gridCols} onChange={e => { touch(); const c = Math.max(1, Math.min(6, +e.target.value || 1)); setFormData(fd => ({ ...fd, gridCols: c })); setGridAnswers(prev => rebuildGrid(formData.gridRows, c, prev)); }} style={{ width: 64, padding: '6px 8px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--card)', color: 'var(--text-1)' }} />
                </label>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>Можно вставить все ответы сразу: скопируйте их через пробел или переносы и вставьте в первую ячейку.</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <tbody>
                    {Array.from({ length: formData.gridRows }, (_, r) => (
                      <tr key={r}>
                        {Array.from({ length: formData.gridCols }, (_, c) => (
                          <td key={c} style={{ padding: 3, border: '1px solid var(--border)' }}>
                            <input type="text" value={gridAnswers[r]?.[c] ?? ''} onChange={e => { touch(); const val = e.target.value; setGridAnswers(prev => { const next = prev.map(row => [...row]); if (!next[r]) next[r] = Array(formData.gridCols).fill(''); next[r][c] = val; return next; }); }}
                              onPaste={e => {
                                const tokens = e.clipboardData.getData('text').split(/\s+/).filter(Boolean);
                                if (tokens.length <= 1) return;
                                e.preventDefault();
                                const rows = formData.gridRows, cols = formData.gridCols;
                                setGridAnswers(prev => {
                                  const next = Array.from({ length: rows }, (_, rr) => Array.from({ length: cols }, (_, cc) => prev[rr]?.[cc] ?? ''));
                                  let idx = 0;
                                  for (let p = r * cols + c; p < rows * cols && idx < tokens.length; p++, idx++) next[Math.floor(p / cols)][p % cols] = tokens[idx];
                                  return next;
                                });
                                touch();
                              }}
                              placeholder={`[${r+1},${c+1}]`} style={{ width: '100%', padding: '7px 9px', border: 'none', background: 'rgba(124,77,255,0.05)', fontSize: 13, outline: 'none', minWidth: 80, boxSizing: 'border-box', color: 'var(--text-1)' }} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="fl-section"><span className="ln" /><span className="tx">Решение</span><span className="ln" /></div>

          <div className="fl">
            <label className="fl-label">Текстовое решение</label>
            <RichEditor
              value={formData.textSolution}
              onChange={(v) => { if (v !== formData.textSolution) touch(); setFormData(fd => ({ ...fd, textSolution: v })); }}
              placeholder=""
              onImageUpload={imgUpload}
              onAttachFile={(f) => handleAddFile(f, 'solution')}
            />
            {attachedSolutionFiles.length > 0 && (
              <div className="mat-list2" style={{ marginTop: 8 }}>
                {attachedSolutionFiles.map((file, i) => (
                  <div key={i} className="mat-item2">
                    <span className="mat-ic2" style={{ background: fileColor(file.name)[0], color: fileColor(file.name)[1] }}><FileIcon name={file.name} size={18} /></span>
                    <a className="mat-name2" href={file.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-1)', textDecoration: 'none' }}>{file.name}</a>
                    {fmtSize(file.size) && <span className="mat-size2">{fmtSize(file.size)}</span>}
                    <button className="mat-del2" aria-label="Удалить" onClick={() => { setAttachedSolutionFiles(prev => prev.filter((_, k) => k !== i)); touch(); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="fl">
            <label className="fl-label"><span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg></span>Ссылка на видеорешение <span className="opt">— необязательно</span></label>
            <div className="fl-iwrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
              <input className="fl-input with-ic" value={formData.videoSolution} onChange={(e) => set({ videoSolution: e.target.value })} />
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
});

export default HomeworkNumberEditor;
